import cookie from 'js-cookie';
import { BYBIT_GUID_KEY } from './config/storage';
import { domain } from './env';
import { sample, times } from 'lodash-es';

// 生成随机 Trace ID
export const genTraceID = () => {
  const HEX_DIG_POOL = '0123456789abcdef'.split('');
  const getRandomNum = () => sample(HEX_DIG_POOL);
  const getRandomId = (bits: number) => times(bits, getRandomNum).join('');
  return `00-${getRandomId(32)}-${getRandomId(16)}-01`;
};

/**
 * GUID
 */
function s4() {
  return Math.floor((1 + Math.random()) * 0x10000)
    .toString(16)
    .substring(1);
}

export function getGuid() {
  return `${s4()}${s4()}-${s4()}-${s4()}-${s4()}-${s4()}${s4()}${s4()}`;
}

export const guid = (): string => {
  let value = cookie.get('BYBIT_GUID_KEY');
  if (!value || value.length !== 36) {
    value = getGuid();
  }
  cookie.set(BYBIT_GUID_KEY, value as string, {
    expires: 120,
    domain: domain,
    secure: false,
  });

  return value;
};

/**
 * 执行一个函数，并返回结果（相对优雅的立即执行函数）
 * @param fn 要执行的函数
 * @returns 结果
 */
export function invoke<T>(fn: () => T) {
  return fn();
}
export function safeInvoke<T>(fn: () => T, defaultValue: T): T {
  try {
    return fn();
  } catch (e) {
    console.error(e);
    return defaultValue;
  }
}

export function safeParseJSON<T>(
  json: string,
  defaultValue?: T
): T | undefined {
  try {
    return JSON.parse(json);
  } catch (e) {
    return defaultValue;
  }
}

export async function resolvePromise<T, Error = any>(
  promise: Promise<T>,
  timeout?: number
) {
  return new Promise<[null, Awaited<T>] | [Error, null]>((resolve) => {
    if (timeout) {
      const timer = setTimeout(() => {
        const err = [new Error('promise timeout'), null] as [Error, null];
        resolve(err);
      }, timeout);
    } else {
      promise
        .then((res) => resolve([null, res as Awaited<T>]))
        .catch((err) => resolve([err, null]));
    }
  });
}

export async function runAsyncSafe<T = any, Error = any>(
  promise: Promise<T> | (() => Promise<T>),
  timeout?: number
) {
  return await resolvePromise<T, Error>(
    typeof promise === 'function'
      ? new Promise((resolve, reject) => {
          try {
            resolve(promise());
          } catch (err) {
            reject(err);
          }
        })
      : promise,
    timeout
  );
}

export async function sleep(number: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, number));
}

export async function timeout(
  time: number,
  message = `${time}ms timeout.`
): Promise<void> {
  return sleep(time).then(() => Promise.reject(new Error(message)));
}

export function retryWithFibonacci<T>(
  promiseFunc: () => Promise<T>,
  maxRetries: number
): Promise<T> {
  let attempts = 0;
  let fib1 = 1;
  let fib2 = 1;

  function nextFibonacci(): number {
    const next = fib1 + fib2;
    fib1 = fib2;
    fib2 = next;
    return fib1;
  }

  function tryPromise(): Promise<T> {
    return new Promise((resolve, reject) => {
      promiseFunc()
        .then(resolve)
        .catch((error) => {
          // console.error(error);
          if (attempts < maxRetries) {
            attempts++;
            const waitTime = nextFibonacci();
            console.log(`Retry #${attempts} in ${waitTime} seconds`);
            setTimeout(() => {
              tryPromise().then(resolve).catch(reject);
            }, waitTime * 1000);
          } else {
            reject(`Failed after ${maxRetries} retries: ${error}`);
          }
        });
    });
  }

  return tryPromise();
}

export const capitalize = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const addDarkTheme = () => {
  document.querySelector('html').classList.add('moly-theme-dark');
};

export const onlyRunOnce = <T extends (...args: any[]) => any>(fn: T) => {
  let called = false;
  let rs: ReturnType<T>;
  return (...args: Parameters<T>) => {
    if (called) {
      return rs;
    }
    rs = fn(...args);
    called = true;
    return rs;
  };
};

export const onlyCallOnceInOneTick = (fn: (...args: any[]) => any) => {
  let thread: Promise<void>;
  let _args: any[] = [];
  // 本来想用 promise resolve 想了下算了用setTimeout 简单替代吧，以后有问题再说
  return (...args: any[]) => {
    _args = args;
    if (thread) {
      return thread;
    }
    thread = new Promise((resolve) => {
      setTimeout(() => {
        runAsyncSafe(() => fn(..._args)).then(([err]) => {
          if (err) {
            console.error(err);
          }
          resolve(undefined);
        });
      });
    }).then(() => {
      thread = undefined;
      _args = [];
    });
    return thread;
  };
};

export const addListener = (
  eventBus: any,
  event: string,
  fn: (...args: any[]) => void,
  evtOpts?: unknown
) => {
  const add =
    eventBus.on ||
    eventBus.addListener ||
    eventBus.addEventListener ||
    eventBus.$on;
  add.call(eventBus, event, fn, evtOpts);
  return () => {
    const remove =
      eventBus.off ||
      eventBus.removeListener ||
      eventBus.removeEventListener ||
      eventBus.$off;
    remove.call(eventBus, event, fn);
  };
};

export const nonceContainer = () => {
  const nonceRef = {
    current: 0,
  };
  return () => {
    nonceRef.current = nonceRef.current + 1;
    const current = nonceRef.current;
    return {
      value: current,
      isValid: () => current === nonceRef.current,
    };
  };
};
export type NonceGenerator = () => { value: number; isValid: () => boolean };

/** 模板字符串替换 */
export const replaceVar = (
  template: string,
  context: Record<string, string> = {},
  {
    doubleBrace = false,
    keepVarName = true,
  }: {
    doubleBrace?: boolean;
    keepVarName?: boolean;
  } = {}
) => {
  const reg = doubleBrace ? /\{\{(.*?)\}\}/g : /\{(.*?)\}/g;
  return template.replace(
    reg,
    (match, key) => context[key] ?? (keepVarName ? match : '')
  );
};

export const replaceBybitToLocationHost = (url: string = '') => {
  return url.replace('www.bybit.com', window.location.host);
};

// 判断是否为荷兰站
export const isNlSite = () => {
  const { hostname, pathname } = window.location;

  // 线上环境
  if (
    hostname.endsWith('.nl') &&
    (pathname.includes('en-NL') || pathname.includes('nl-NL'))
  ) {
    return true;
  }

  // 开发/测试/预发环境 没有.nl域名
  if (
    (hostname.includes('local') ||
      hostname.includes('sbu-test-5') ||
      hostname.includes('bycloud')) &&
    (pathname.includes('en-NL') || pathname.includes('nl-NL'))
  ) {
    return true;
  }

  return false;
};
